import { useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useClinicById } from '@/hooks/useClinicById'
import { useClinicGroup } from '@/hooks/useClinicGroups'
import { useMe } from '@/hooks/useMe'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Building2 } from 'lucide-react'
import { PageHeader } from '@/components/PageHeader'
import ClinicInfo from './ClinicInfo'
import { JoinClinicGroupDialog } from './JoinClinicGroupDialog'

export default function ClinicPage() {
  const location = useLocation()
  const params = useParams()
  // 路徑可能是 /admin/clinic/create、/provider/:id/clinic/create、或 /clinic/:clinic_id/info
  // 用 endsWith 偵測 create，clinic_id 直接從 useParams 拿
  const isCreateMode = location.pathname.endsWith('/clinic/create')
  const clinicId = isCreateMode ? undefined : params.clinic_id
  const { clinic, isLoading, mutate } = useClinicById(clinicId)
  const { user } = useMe()
  const { clinicGroup } = useClinicGroup(clinic?.clinic_group_id ?? null)
  const [joinDialogOpen, setJoinDialogOpen] = useState(false)

  const canJoinGroup = user?.role_type === 'sys_admin' || user?.role_type === 'provider_admin'

  // 建立模式為頂層操作（從 list 進來），用 page level
  // 編輯模式是在 clinic scope 裡，側邊 Logo 已顯示診所名 → section level
  const headerLevel = isCreateMode ? 'page' : 'section'
  const title = isCreateMode ? '新增診所' : '診所資訊'
  const description = isCreateMode ? '建立新的診所資訊' : '管理診所資訊和人員'

  return (
    <div className="space-y-6">
      <PageHeader title={title} description={description} level={headerLevel}>
        {!isCreateMode && clinic && (
          <>
            {clinic.clinic_group_id && clinicGroup ? (
              <Badge variant="secondary" className="gap-1.5">
                <Building2 className="h-3 w-3" />
                {clinicGroup.group_name}
              </Badge>
            ) : (
              canJoinGroup && (
                <Button variant="outline" size="sm" onClick={() => setJoinDialogOpen(true)}>
                  <Building2 className="h-4 w-4 mr-1.5" />
                  加入集團
                </Button>
              )
            )}
          </>
        )}
      </PageHeader>

      <ClinicInfo clinic={clinic} isLoading={isLoading} mode={isCreateMode ? 'create' : 'edit'} />

      {clinic && (
        <JoinClinicGroupDialog
          open={joinDialogOpen}
          onOpenChange={setJoinDialogOpen}
          clinicId={clinic.clinic_id}
          providerId={clinic.provider_id}
          onSuccess={() => mutate()}
        />
      )}
    </div>
  )
}
